import clsx from 'clsx'
import { FlagShortPole, FlagShortPoleFilled, OpenNew } from '@/assets/icons'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { type WarningCaseStatus } from '@/citator/schema/case-status-schema'
import { kInlineTreatmentFlagFrameClasses, kInlineTreatmentFlagGlyphClasses, kCaseTreatmentVisualsByStatus } from '@/citator/utils/case-treatment-visuals'
import { kCaseStatusDefinitions } from '@/citator/utils/case-status-definitions'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { mapCaseStatusToBadgeType, type CitatorSurfaceInline } from '@/analytics/enums/citator.enums'
import { trackCitatorBadgeClicked } from '@/analytics/utils/citator-analytics'

/** The hover tooltip appears after ~1s. */
const kEnterDelayMs = 1000

type InlineTreatmentFlagProps = {
  caseStatus: WarningCaseStatus
  surface: CitatorSurfaceInline
  /** Opens the citator panel on the Citation Analysis view for this authority. */
  onViewCitationAnalysis: () => void
  className?: string
}

/**
 * Always-visible treatment flag rendered beside an inline citation (warning
 * statuses only — callers gate with `isWarningCaseStatus` upstream). Hover shows
 * the status definition; clicking the flag or the definition's link opens the
 * citator panel via `onViewCitationAnalysis`.
 *
 * The component owns click analytics only. Impressions are container-owned and
 * authority-denominated — the chat markdown and the canvas editor host each fire
 * one impression per warning-status authority per surface session — so per-mention
 * instances of this component stay silent on render.
 *
 * Sized in em (18px frame / 13.5px glyph at the 14px chat spec) so the flag stays
 * proportionate to whatever prose size the surface renders.
 */
export function InlineTreatmentFlag({ caseStatus, surface, onViewCitationAnalysis, className }: InlineTreatmentFlagProps) {
  const { colorClass, filled } = kCaseTreatmentVisualsByStatus[caseStatus]
  const Icon = filled ? FlagShortPoleFilled : FlagShortPole
  const caseStatusDefinition = kCaseStatusDefinitions[caseStatus]
  const badgeType = mapCaseStatusToBadgeType(caseStatus)

  const { trackEvent } = useAnalytics()

  const handleViewCitationAnalysis = () => {
    trackCitatorBadgeClicked({ trackEvent, surface, badgeType })
    onViewCitationAnalysis()
  }

  return (
    <CustomizableTooltip
      arrow
      enterDelay={kEnterDelayMs}
      // Without this, MUI drops the delay to 0 for a while after any tooltip opens —
      // sweeping the cursor across flag-dense prose would pop tooltips instantly.
      enterNextDelay={kEnterDelayMs}
      // No hover on touch devices — a tap goes straight to the citator panel.
      disableTouchListener
      // The definition describes the flag; the button's own aria-label (the status
      // heading) stays its accessible name. Without this, MUI aria-labelledby's the
      // trigger with the whole tooltip text while open.
      describeChild
      tooltipClassName="!max-w-none !p-5 !bg-white !rounded-md !text-brand-neutral-950 !font-sans border border-brand-neutral-200 shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)]"
      arrowClassName="!text-white !w-4 !h-2 [&::before]:border [&::before]:border-brand-neutral-200"
      title={
        <div className="flex flex-col gap-1 max-w-[420px] text-sm font-normal text-brand-neutral-950">
          <p className="text-sm font-semibold leading-5 tracking-[-0.15px]">{caseStatusDefinition.heading}</p>
          {caseStatusDefinition.body.map((paragraph, index) => (
            <p key={index} className="text-xs font-normal leading-4">
              {paragraph}
            </p>
          ))}
          <button
            type="button"
            onClick={handleViewCitationAnalysis}
            className="inline-flex items-center gap-0.5 self-start mt-1 text-xs font-medium text-brand-700 underline hover:text-brand-800 cursor-pointer"
          >
            View citation analysis
            <OpenNew className="w-3.5 h-3.5" />
          </button>
        </div>
      }
    >
      <button
        type="button"
        onClick={handleViewCitationAnalysis}
        aria-label={caseStatusDefinition.heading}
        className={clsx(kInlineTreatmentFlagFrameClasses, colorClass, className)}
      >
        <Icon aria-hidden className={kInlineTreatmentFlagGlyphClasses} />
      </button>
    </CustomizableTooltip>
  )
}
